import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api';
import { OnboardingTarget } from '../components/onboarding';
import { useOnboarding, ONBOARDING_TOOLTIPS } from '../hooks/useOnboarding';

interface Session {
  id: string;
  title: string;
  status: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  coverageScore: number | null;
  itemsTotal: number | null;
  itemsCovered: number | null;
}

interface SessionsResponse {
  sessions: Session[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export function Sessions() {
  const { get, delete: del } = useApi();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Onboarding
  const { shouldShowTooltip, dismissTooltip, skipAll, isLastTooltip } = useOnboarding();
  const showSessionsTip = shouldShowTooltip(ONBOARDING_TOOLTIPS.SESSIONS);

  async function fetchSessions(page = 1) {
    try {
      setLoading(true);
      const data = await get<SessionsResponse>(`/sessions?page=${page}&limit=20`);
      setSessions(data.sessions);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSessions();
  }, []);

  async function handleDelete(sessionId: string) {
    if (!confirm('Are you sure you want to delete this session?')) return;
    try {
      await del(`/sessions/${sessionId}`);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err) {
      alert('Failed to delete session');
    }
  }

  function formatDuration(ms: number | null): string {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m ${seconds % 60}s`;
  }

  if (loading && sessions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with onboarding tooltip */}
      <OnboardingTarget
        tooltipId="sessions"
        tooltipTitle="Session History"
        tooltipDescription="All your past sessions are stored here. Click any session to see the full details, transcript, and coverage breakdown."
        tooltipPosition="bottom"
        show={showSessionsTip}
        onDismiss={() => dismissTooltip(ONBOARDING_TOOLTIPS.SESSIONS)}
        onSkipAll={skipAll}
        isLast={isLastTooltip(ONBOARDING_TOOLTIPS.SESSIONS)}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Session History</h1>
            <p className="mt-1 text-slate-400">
              {pagination.total} total sessions
            </p>
          </div>
          <Link
            to="/app"
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
          >
            New Session
          </Link>
        </div>
      </OnboardingTarget>

      {/* Sessions list */}
      {sessions.length > 0 ? (
        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Session
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Coverage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {sessions.map((session) => {
                const date = new Date(session.startedAt);
                const score = Number(session.coverageScore ?? 0);
                const coverageColor =
                  score >= 80
                    ? 'text-green-500'
                    : score >= 50
                    ? 'text-yellow-500'
                    : 'text-red-500';

                return (
                  <tr key={session.id} className="hover:bg-slate-700/50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/sessions/${session.id}`}
                        className="text-white hover:text-green-400 font-medium"
                      >
                        {session.title || 'Untitled Session'}
                      </Link>
                      <p className="text-sm text-slate-500">
                        {session.itemsCovered ?? 0}/{session.itemsTotal ?? 0} items covered
                      </p>
                    </td>
                    <td className="px-6 py-4 text-slate-300">
                      {date.toLocaleDateString()}
                      <br />
                      <span className="text-sm text-slate-500">
                        {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-300">
                      {formatDuration(session.durationMs)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-lg font-bold ${coverageColor}`}>
                        {Math.round(score)}%
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          session.status === 'completed'
                            ? 'bg-green-900/50 text-green-400'
                            : session.status === 'active'
                            ? 'bg-blue-900/50 text-blue-400'
                            : 'bg-slate-700 text-slate-400'
                        }`}
                      >
                        {session.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDelete(session.id)}
                        className="text-slate-400 hover:text-red-400"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-slate-800 rounded-lg p-8 text-center border border-slate-700">
          <p className="text-slate-400">No sessions found</p>
          <Link
            to="/app"
            className="mt-4 inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
          >
            Start your first session
          </Link>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => fetchSessions(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="px-4 py-2 bg-slate-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-slate-400">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => fetchSessions(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            className="px-4 py-2 bg-slate-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
